import axios from "axios";
import { compressImage } from "../utils/compressImage";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://driverbe.mistyride.com/";
// "http://localhost:8080/";
// "https://driverbe.mistyride.com/";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error("API Response Error:", error.response?.data || error.message);

    // Handle network errors
    if (!error.response) {
      throw new Error("Network error. Please check your connection.");
    }

    // Handle specific error cases
    const status = error.response.status;
    const data = error.response.data;

    if (status === 404) {
      throw new Error("Ride not found");
    } else if (status === 429) {
      throw new Error("Too many requests. Please wait before trying again.");
    } else if (status >= 500) {
      throw new Error("Server error. Please try again later.");
    } else if (data?.message) {
      throw new Error(data.message);
    } else if (data?.details?.length) {
      const first = data.details[0];
      const msg = first?.message || data.error || "Validation failed";
      throw new Error(msg);
    } else {
      throw new Error("An unexpected error occurred");
    }
  },
);

export const rideAPI = {
  // Get ride details and current status
  getRideDetails: async (rideId, recordId) => {
    const response = await api.get(`/api/ride/${rideId}&${recordId}`);
    return response.data;
  },

  // Update driver status
  updateStatus: async (rideId, recordId, statusData) => {
    const response = await api.post(`/api/ride/${rideId}/status`, statusData, {
      params: { record_id: recordId, ride_id: rideId },
    });
    return response.data;
  },

  // Submit driver info (write-once)
  submitDriverInfo: async (rideId, recordId, fields) => {
    const response = await api.post(`/api/ride/${rideId}/driver-info`, fields, {
      params: { record_id: recordId },
    });
    return response.data;
  },

  // Submit vehicle info (write-once)
  submitVehicleInfo: async (rideId, recordId, fields) => {
    const response = await api.post(
      `/api/ride/${rideId}/driver-vehicle-info`,
      fields,
      {
        params: { record_id: recordId },
      },
    );
    return response.data;
  },
};

export const uploadAPI = {
  // Get signed upload URL
  getUploadUrl: async (filename, contentType, rideId) => {
    const response = await api.post("/api/upload-url", {
      filename,
      contentType,
      ride_id: rideId,
    });
    return response.data;
  },

  // Upload file to GCS
  uploadFile: async (file, uploadUrl) => {
    try {
      console.log("Uploading file:", {
        name: file.name,
        size: file.size,
        type: file.type,
        url: uploadUrl.substring(0, 100) + "...",
      });

      const response = await axios.put(uploadUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
        maxContentLength: 10 * 1024 * 1024, // 10MB
        maxBodyLength: 10 * 1024 * 1024, // 10MB
        timeout: 30000, // 30 seconds
      });

      console.log("Upload successful:", response.status);
      return response;
    } catch (error) {
      console.error("Upload failed:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  },

  // Compress, get a signed URL, upload; returns the photo's public URL.
  // name: optional filename (e.g. "driver_selfie.jpg"), defaults to the file's name
  uploadPhoto: async (file, rideId, name) => {
    const photo = await compressImage(file);
    console.log(
      `Photo ${file.name}: ${Math.round(file.size / 1024)}KB -> ${Math.round(photo.size / 1024)}KB`,
    );
    const urlResponse = await uploadAPI.getUploadUrl(
      `${Date.now()}_${name || photo.name}`,
      photo.type,
      rideId,
    );
    if (!urlResponse.success) {
      throw new Error("Failed to get upload URL");
    }
    await uploadAPI.uploadFile(photo, urlResponse.uploadUrl);
    return urlResponse.publicUrl;
  },
};

export default api;
